import { toBcp47 } from "@/locales/bcp47.js";

/**
 * Format a date as a long, human-readable day in the locale the UI is showing.
 *
 * The user-facing date sites in this app were all pinned to the en-US locale,
 * which renders "January 5, 2026" to a reader whose entire interface is in
 * Japanese. The locale is a required-in-practice argument rather than something
 * this module reaches for, so the helper stays pure and importable without
 * vue-i18n; `tests/unit/localizedDates.test.js` fails any call site that omits
 * it.
 *
 * @param {string|number|Date} value - anything `new Date()` accepts
 * @param {string} [locale] - one of this app's locale codes, e.g. "pt-br".
 *   Omitted, the runtime default is used, which is only correct in a context
 *   that has no UI locale (there is none in the app today).
 * @returns {string} the formatted date, or "" when `value` is not a real date.
 *   Empty rather than the literal "Invalid Date": that string is English, is not
 *   translatable, and means nothing to a reader in any locale.
 */
export const formatLongDate = (value, locale) => {
  const date = value instanceof Date ? value : new Date(value ?? NaN);
  if (Number.isNaN(date.getTime())) return "";
  const tag = toBcp47(locale);
  return date.toLocaleDateString(tag || undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

/**
 * Gets the timestamp for the start of the current calendar month (the 1st at 00:00:00 local time)
 * @returns {number} Unix timestamp in seconds
 */
export const getStartOfMonth = () => {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
  return Math.floor(startOfMonth.getTime() / 1000);
};

/**
 * Gets the timestamp for the start of the current week (Monday at 00:00:00)
 * @returns {number} Unix timestamp in seconds
 */
export const getStartOfWeek = () => {
  const DAYS_IN_WEEK = 7;
  const MONDAY_OFFSET = 1; // Monday is 1 in getDay()

  const now = new Date();
  const currentDay = now.getDay();
  const daysToMonday =
    (currentDay + (DAYS_IN_WEEK - MONDAY_OFFSET)) % DAYS_IN_WEEK;

  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - daysToMonday);
  startOfWeek.setHours(0, 0, 0, 0);
  return Math.floor(startOfWeek.getTime() / 1000);
};
